/**
 * PricingTab — Run pricing via Pricing Engine, compare products and rate grids.
 * Consumes API shape: { loan: LoanInfoViewModel, borrowers, property }
 */
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Alert, Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, Collapse, Divider, FormControlLabel, Grid, IconButton, MenuItem, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockIcon from '@mui/icons-material/Lock';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SettingsIcon from '@mui/icons-material/Settings';
import CurrencyInput from '../../../components/shared/CurrencyInput';
import SectionHeader from '../../../components/shared/SectionHeader';
import {
  appliesCountyLoanLimit,
  applyRepresentativeFicoToPricingLoan,
  evaluateCountyLoanLimits,
  fetchCountyLimitsForPricing,
  getLoanPropertyInfo,
  getPricingLoanTermOptions,
  mergeTermsForNonQmPricing,
  productFamiliesIncludeNonQm,
  resolveMortgageTypeToPricingFamily,
} from '../../../services/pricingEngineService';
import pricingApi, { runPricingWithEngine } from '../../../services/pricingApi';
import configurationApi from '../../../services/configurationApi';
import { useLazyGetCountyLoanLimitQuery } from '../../../services/countyLoanLimitApi';
import brokerCompensationApi from '../../../services/brokerCompensationApi';
import loansApi from '../../../services/loansApi';
import loanPricingApi from '../../../services/loanPricingApi';
import { useToast } from '../../../components/shared/Toast';
import { useConfirmModal } from '../../../components/shared/ConfirmModal';
import useLockDeskStatus from '../../../hooks/useLockDeskStatus';
import LockDeskClosedAlert from '../../../components/shared/LockDeskClosedAlert';
import StatusBadge, { ELIGIBILITY_MAP } from '../../../components/shared/StatusBadge';
import { ProductCard, IneligibleReasonsPanel, fmt, fmtDollar, fmtPct, PAR_PRICE_TARGET } from '../../../components/shared/pricing';
import { NearParBadge, NearParRateLabel } from '../../../components/shared/pricing/NearParIndicators';
import { filterIneligibleReasonStrings, hasDisplayableEligibilityAnalysis, isLenderIdIneligibleParam } from '../../../utils/ineligibleReasons';
import { getCashOutAmount, isCashOutRefinanceFromApi } from '../../../utils/loanPurposeDisplay';
import { getAxiosErrorMessage } from '../../../utils/getAxiosErrorMessage';
import {
  isDscrDocType,
  mapNonQmBankStatementToEngine,
  mapNonQmDocTypeToEngine,
} from '../../../utils/nonQm';

const LOCK_DURATIONS = [15, 30, 45];
const AMORTIZATION_TYPES = [
  { id: 'Fixed', label: 'Fixed' },
  { id: 'ARM', label: 'ARM' },
];
const PRODUCT_FAMILIES = [
  { id: 'Conventional', label: 'Conventional' },
  { id: 'FHA', label: 'FHA' },
  { id: 'VA', label: 'VA' },
  { id: 'USDA', label: 'USDA' },
  { id: 'Non-QM', label: 'Non-QM' },
];
const NON_QM_FAMILY_ID = 'Non-QM';

/** Matches PRODUCT_FAMILIES id and loan `nonQualifiedMortgageIndicator` (boolean or form string). */
const loanIsNonQmForPricing = (loan) => {
  if (!loan) return false;
  const n = loan.nonQualifiedMortgageIndicator;
  return n === true || n === 'True';
};

const PricingTab = ({ loanDetail, loanId, pricingRequest, brokerCompany, isReadOnly = false, hasUnsavedChanges = false, initialFilters, autoRun, onAutoRunComplete, lockMode = false, onLockModeComplete, onNavigateTab, showAdvisorQuickActions = false }) => {
  // ── Derive product assignment info from loan ──
  const assignedProduct = useMemo(() => {
    const l = loanDetail?.loan || {};
    return l.productCode || null;
  }, [loanDetail]);

  // ── Auto-seed filters from loan data ──
  const autoSeededFilters = useMemo(() => {
    const mergeNonQmFamilies = (families) => {
      const arr = [...(families || [])];
      const l = loanDetail?.loan;
      if (loanIsNonQmForPricing(l) && !arr.includes(NON_QM_FAMILY_ID)) arr.push(NON_QM_FAMILY_ID);
      return arr;
    };

    if (initialFilters) {
      return {
        ...initialFilters,
        selectedFamilies: mergeNonQmFamilies(initialFilters.selectedFamilies),
      };
    }
    const l = loanDetail?.loan || {};
    const seeded = {};
    if (assignedProduct) {
      // Product is assigned — match its family and term
      const family = resolveMortgageTypeToPricingFamily(l.mortgageType);
      seeded.selectedFamilies = family ? [family] : [];
      const termYears = l.loanTermMonths ? Math.round(l.loanTermMonths / 12) : null;
      const termOptions = getPricingLoanTermOptions(seeded.selectedFamilies);
      seeded.selectedTerms = termYears && termOptions.includes(termYears) ? [termYears] : [];
    } else {
      // No product assigned — match mortgageType → families, term → terms
      const family = resolveMortgageTypeToPricingFamily(l.mortgageType);
      seeded.selectedFamilies = family ? [family] : [];
      const termYears = l.loanTermMonths ? Math.round(l.loanTermMonths / 12) : null;
      const termOptions = getPricingLoanTermOptions(seeded.selectedFamilies);
      seeded.selectedTerms = termYears && termOptions.includes(termYears) ? [termYears] : [];
    }
    seeded.selectedFamilies = mergeNonQmFamilies(seeded.selectedFamilies);
    return seeded;
  }, [loanDetail, initialFilters, assignedProduct]);

  // ── Filters state (seeded from loan data or initialFilters) ──
  const [lockDuration, setLockDuration] = useState(autoSeededFilters?.lockDuration ?? 30);
  const [selectedFamilies, setSelectedFamilies] = useState(autoSeededFilters?.selectedFamilies ?? []);
  const [selectedTerms, setSelectedTerms] = useState(autoSeededFilters?.selectedTerms ?? []);
  const [compensationType, setCompensationType] = useState(autoSeededFilters?.compensationType ?? 'LPC');
  const [bpcCalcMethod, setBpcCalcMethod] = useState(autoSeededFilters?.bpcCalcMethod ?? 'PercentOfLoan');
  const [bpcValue, setBpcValue] = useState(autoSeededFilters?.bpcValue ?? '');
  const [selectedAmortTypes, setSelectedAmortTypes] = useState(autoSeededFilters?.selectedAmortTypes ?? ['Fixed']);
  const [showFilters, setShowFilters] = useState(true);

  const loanTermOptions = useMemo(
    () => getPricingLoanTermOptions(selectedFamilies),
    [selectedFamilies],
  );

  useEffect(() => {
    if (!productFamiliesIncludeNonQm(selectedFamilies)) {
      setSelectedTerms((prev) => {
        const next = prev.filter((t) => t !== 40);
        return next.length > 0 ? next : [30];
      });
    }
  }, [selectedFamilies]);

  const nonQmIndicator = loanDetail?.loan?.nonQualifiedMortgageIndicator;
  useEffect(() => {
    setSelectedFamilies((prev) => {
      const want = nonQmIndicator === true || nonQmIndicator === 'True';
      if (want && !prev.includes(NON_QM_FAMILY_ID)) return [...prev, NON_QM_FAMILY_ID];
      if (!want && prev.includes(NON_QM_FAMILY_ID)) return prev.filter((f) => f !== NON_QM_FAMILY_ID);
      return prev;
    });
  }, [nonQmIndicator]);

  // ── Pricing state ──
  const [products, setProducts] = useState([]);
  const [lastPricingRequest, setLastPricingRequest] = useState(null);
  const [pricingError, setPricingError] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [quotedAt, setQuotedAt] = useState(null);
  const [rateSheetDate, setRateSheetDate] = useState(null);
  const [pricingSnapshot, setPricingSnapshot] = useState(null);
  const [eligibleOnly, setEligibleOnly] = useState(true);
  const [sortBy, setSortBy] = useState('price');
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [selectedRate, setSelectedRate] = useState(null);
  const [showAllRates, setShowAllRates] = useState(false);
  const selectedRateDetailRef = useRef(null);

  // ── Assign product state ──
  const [showAssignConfirm, setShowAssignConfirm] = useState(false);
  const [assignError, setAssignError] = useState(null);
  const [assignSuccess, setAssignSuccess] = useState(null);
  const [assignProduct] = loansApi.endpoints.assignProduct.useMutation();
  const [assignAndLockProduct] = loansApi.endpoints.assignAndLockProduct.useMutation();
  const [runPricing] = pricingApi.endpoints.runPricing.useMutation();
  const [runLoanPricing] = pricingApi.endpoints.runLoanPricing.useMutation();
  const { data: featureFlags = {} } = configurationApi.endpoints.getFeatureFlags.useQuery();
  const [isAssigning, setIsAssigning] = useState(false);
  const [isAssigningAndLocking, setIsAssigningAndLocking] = useState(false);

  // ── Lock rate state ──
  const [assignedQuoteId, setAssignedQuoteId] = useState(null);
  const [isLocking, setIsLocking] = useState(false);
  const [requestRateLock] = loanPricingApi.endpoints.requestRateLock.useMutation();
  const { isLockDeskEnabled, lockDeskStatus } = useLockDeskStatus();
  const { showToast, ToastContainer } = useToast();
  const { confirm, ConfirmModalContainer } = useConfirmModal();

  // ── Check if loan is already locked ──
  const loan = loanDetail?.loan || {};
  const isLoanLocked = (() => {
    if (loan.lockConfirmedDate) return true;
    if (loan.lockExpirationDate) {
      const exp = new Date(loan.lockExpirationDate);
      return !isNaN(exp.getTime()) && exp > new Date();
    }
    return false;
  })();
  const isReadOnlyUnlocked = isReadOnly && !isLoanLocked;

  const brokerageIdForCompensation = brokerCompany?.id || null;

  const { data: activeCompensation, isLoading: isLoadingComp } = brokerCompensationApi.endpoints.getActiveCompensation.useQuery(
    brokerageIdForCompensation,
    { skip: !brokerageIdForCompensation }
  );
  const isLpc = compensationType === 'LPC';
  const noActiveComp = isLpc && !isLoadingComp && !activeCompensation;

  // ── Lock Mode: pre-populate from loan's current product data + check freshness ──
  const lockModeFired = useRef(false);
  const lockModePendingRunRef = useRef(false);
  const lockModePreserveSelectionRef = useRef(false);
  const lockModeScrollAfterRunRef = useRef(false);
  useEffect(() => {
    if (lockMode && loanDetail && !lockModeFired.current) {
      lockModeFired.current = true;
      const l = loanDetail.loan || {};
      const hasProduct = !!(l.programName || l.productCode);

      if (hasProduct) {
        // Pre-populate selectedRate from loan's current product data
        setSelectedRate({
          productName: l.programName || l.productCode,
          productShortName: l.productCode || l.programName,
          rate: l.noteRate,
          adjustedPrice: l.loanOfficerPrice,
          basePrice: l.basePrice,
          term: l.loanTermMonths ? l.loanTermMonths / 12 : 30,
          family: l.mortgageType || '',
          typeName: l.amortizationType || '',
        });
        setAssignSuccess(`${l.programName || l.productCode} at ${l.noteRate != null ? fmtPct(l.noteRate) : '—'} is assigned to this loan.`);
        if (l.quoteId) setAssignedQuoteId(l.quoteId);
        if (l.rateLockPeriod) setLockDuration(l.rateLockPeriod);
        lockModePendingRunRef.current = true;
        lockModePreserveSelectionRef.current = true;
        lockModeScrollAfterRunRef.current = true;
      }
      onLockModeComplete?.();
    }
  }, [lockMode, loanDetail, onLockModeComplete, compensationType, brokerageIdForCompensation]);

  // Reset lockModeFired when lockMode toggles off
  useEffect(() => {
    if (!lockMode) {
      lockModeFired.current = false;
    }
  }, [lockMode]);

  // ── Loan limit state ──
  const [loanLimitWarning, setLoanLimitWarning] = useState(null);
  const [loanLimitInfo, setLoanLimitInfo] = useState(null);
  const [fetchCountyLimit] = useLazyGetCountyLoanLimitQuery();

  const buildPricingRequestFromLoanDetail = useCallback((requestOverrides = {}) => {
    const baseRequest = pricingRequest;
    if (!baseRequest) return null;

    const request = JSON.parse(JSON.stringify(baseRequest));
    request.loan = request.loan || {};
    request.options = request.options || {};

    // Keep request fresh for each run and mirror UI-selected controls.
    request.loan.submissionDate = new Date().toISOString();
    request.loan.compensationType = compensationType;
    request.options.lockTerm = lockDuration;

    // Pricing engine validates cashoutAmount (lowercase) for cash-out refinances; avoid passing null.
    // The cloned baseRequest may carry the LOS-shaped `cashOutAmount` (camelCase O) — strip it so
    // the payload contains only the pricing-engine key and we don't send the same value twice.
    const loanFromDetail = loanDetail?.loan || {};
    if (isCashOutRefinanceFromApi(loanFromDetail)) {
      const rawCashOut =
        getCashOutAmount(loanFromDetail)
        ?? request.loan.cashoutAmount
        ?? request.loan.cashOutAmount;
      const normalizedCashOut = Number.isFinite(Number(rawCashOut)) ? Number(rawCashOut) : 0;
      request.loan.cashoutAmount = normalizedCashOut;
      delete request.loan.cashOutAmount;
    } else {
      delete request.loan.cashOutAmount;
    }

    // Non-QM Doc Type drives engine product eligibility. Mirror the backend's
    // LoanPricingNonQmMapper so that fresh Non-QM edits flow through immediately,
    // even if the backend-built baseRequest is one save behind.
    const nonQmFromDetail = loanDetail?.nonQm || {};
    const isNonQmLoan = loanIsNonQmForPricing(loanFromDetail);
    if (isNonQmLoan) {
      const altIncomeType = nonQmFromDetail.alternativeIncomeVerificationType;
      const mappedDocType = mapNonQmDocTypeToEngine(altIncomeType);
      if (mappedDocType) request.loan.docType = mappedDocType;

      const bankStatementCode = mapNonQmBankStatementToEngine(
        altIncomeType,
        nonQmFromDetail.alternativeIncomeDocumentationNumberOfMonths,
      );
      request.borrower = request.borrower || {};
      if (bankStatementCode) {
        request.borrower.bankStatement = bankStatementCode;
      } else {
        delete request.borrower.bankStatement;
      }

      if (isDscrDocType(altIncomeType)) {
        const dscr = Number(nonQmFromDetail.subjectPropertyDSCREstimate);
        if (Number.isFinite(dscr) && dscr > 0) {
          request.borrower.debtServiceCoverageRatio = dscr;
        } else {
          delete request.borrower.debtServiceCoverageRatio;
        }
      } else {
        delete request.borrower.debtServiceCoverageRatio;
      }
    } else {
      // Loan is no longer flagged Non-QM — strip any Non-QM-only inputs that may linger
      // from the cached request so the engine doesn't see contradictory data.
      if (request.borrower) {
        delete request.borrower.bankStatement;
        delete request.borrower.debtServiceCoverageRatio;
      }
    }

    // Months of reserves (Vesta-computed, falling back to user-asserted estimate). Engine wants an integer;
    // floor so we never overstate. Always overlay so the editable estimate flows through immediately.
    const monthsReservesSource = loanFromDetail.monthsOfReservesAvailable
      ?? loanFromDetail.monthsOfReservesAvailableEstimate;
    request.borrower = request.borrower || {};
    if (monthsReservesSource != null && Number.isFinite(Number(monthsReservesSource))) {
      request.borrower.monthsReserves = Math.max(0, Math.floor(Number(monthsReservesSource)));
    }

    request.options.productFamilies = requestOverrides.productFamilies || [];
    request.options.productSubFamilies = requestOverrides.productSubFamilies || [];
    request.options.terms = requestOverrides.terms || [];
    request.options.productTypes = requestOverrides.productTypes || [];

    // Always overlay FICO from loan detail — cached pricingRequest can be stale or use PascalCase only.
    applyRepresentativeFicoToPricingLoan(loanDetail, request.loan);

    return request;
  }, [pricingRequest, compensationType, lockDuration, loanDetail]);

  const showStalePricingToast = useCallback((err) => {
    const status = typeof err?.status === 'number' ? err.status : undefined;
    const data = err?.data;
    const code = String(data?.code ?? data?.Code ?? data?.errorCode ?? '');
    /** Same contract as PricingOutdatedLockResponse / stale lock — codes vary slightly by API version. */
    const staleCodes = ['PricingStale', 'PricingOutdated', 'StalePricing', 'PricingOutdatedLock', 'PricingLockStale'];
    if (status === 409 && staleCodes.includes(code)) {
      if (import.meta.env?.DEV) {
        console.warn('[PricingTab] 409 stale pricing / lock', { code, quoteId: data?.quoteId, payload: data });
      }
      showToast('Pricing is outdated. Use Refresh pricing on the Program tile, then try locking again.', 'warning');
      return true;
    }
    return false;
  }, [showToast]);

  // ── Lock Rate ──
  const handleLockRate = async () => {
    if (!assignedQuoteId) return;
    if (!isLockDeskEnabled) {
      showToast('The Lock Desk is currently closed — lock requests cannot be submitted right now.', 'warning');
      return;
    }
    const confirmed = await confirm({
      title: 'Lock Rate',
      message: `Lock ${selectedRate.productName} at ${fmtPct(selectedRate.rate)} for ${lockDuration} days?\n\nOnce locked, changes require post-lock actions (reprice, extension, etc.).`,
      confirmText: 'Lock Rate',
      cancelText: 'Cancel',
      confirmClass: 'btn-success',
    });
    if (!confirmed) return;

    setIsLocking(true);
    try {
      await requestRateLock({ loanId, body: { quoteId: assignedQuoteId } }).unwrap();
      showToast('Rate locked successfully!', 'success');
      setAssignedQuoteId(null);
    } catch (err) {
      console.error('[PricingTab] lock-rate error:', err);
      if (!showStalePricingToast(err)) {
        showToast(getAxiosErrorMessage(err, 'Failed to lock rate.'), 'danger');
      }
    } finally {
      setIsLocking(false);
    }
  };

  // ── Assign & Lock (one step) ──
  // Applies pricing and locks the rate in a single backend call (pro-api#129),
  // separate from the assign-only path.
  const handleAssignAndLock = async () => {
    if (!selectedRate) return;
    const confirmed = await confirm({
      title: 'Assign & Lock',
      message: `Assign ${selectedRate.productName} at ${fmtPct(selectedRate.rate)} and lock for ${lockDuration} days in one step?\n\nOnce locked, changes require post-lock actions (reprice, extension, etc.).`,
      confirmText: 'Assign & Lock',
      cancelText: 'Cancel',
      confirmClass: 'btn-success',
    });
    if (!confirmed) return;

    setIsAssigningAndLocking(true);
    setAssignError(null);
    setAssignSuccess(null);
    try {
      await assignAndLockProduct({
        loanId,
        pricingRequest: lastPricingRequest,
        productId: selectedRate.productId,
        rate: selectedRate.rate,
        price: selectedRate.adjustedPrice,
        lockTerm: lockDuration,
        ...(selectedRate.quoteId ? { quoteId: selectedRate.quoteId } : {}),
        ...(compensationType === 'BPC' && bpcValue ? { bpcCalcMethod, bpcValue: parseFloat(bpcValue) } : {}),
      }).unwrap();
      showToast(`${selectedRate.productName} at ${fmtPct(selectedRate.rate)} assigned and locked.`, 'success');
      // Loan tag invalidation refetches loan detail; reset local pricing state.
      setShowAssignConfirm(false);
      setPricingSnapshot(null);
      setProducts([]);
      setSelectedRate(null);
      setAssignedQuoteId(null);
    } catch (err) {
      console.error('[PricingTab] assign-and-lock error:', err);
      if (!showStalePricingToast(err)) {
        setAssignError(getAxiosErrorMessage(err, 'Failed to assign and lock.'));
      }
    } finally {
      setIsAssigningAndLocking(false);
    }
  };

  // ── Run Pricing ──
  const handleRunPricing = useCallback(async (options = {}) => {
    const preserveSelection = options.preserveSelection === true;
    const previousSelectedRate = options.previousSelectedRate
      ?? (preserveSelection ? selectedRate : null);

    setPricingError(null);
    setProducts([]);
    setExpandedProducts(new Set());
    if (!preserveSelection) {
      setSelectedRate(null);
    }
    setShowAllRates(false);
    setShowAssignConfirm(false);
    setAssignError(null);
    if (!preserveSelection) {
      setAssignSuccess(null);
      setAssignedQuoteId(null);
    }
    setIsRunning(true);

    try {
      // ── Loan limit check (per selected program families) ──
      const { fipsCode, numberOfUnits } = getLoanPropertyInfo(loanDetail);
      const baseLoanAmount = loanDetail?.loan?.loanAmount || 0;
      setLoanLimitWarning(null);
      setLoanLimitInfo(null);

      // Per-family county loan limit check
      const fromMortgage = resolveMortgageTypeToPricingFamily(loanDetail?.loan?.mortgageType);
      const activeFamilies =
        selectedFamilies.length > 0 ? selectedFamilies : fromMortgage ? [fromMortgage] : [];
      const limitFamilies = activeFamilies.filter((f) => appliesCountyLoanLimit(f));
      let families = selectedFamilies.length > 0 ? [...selectedFamilies] : undefined;
      let subFamilies = [];

      /** Agency county limits do not apply to Non-QM — UX only; backend is source of truth. */
      const skipCountyLimitUi = loanIsNonQmForPricing(loanDetail?.loan);

      if (!skipCountyLimitUi && limitFamilies.length > 0 && fipsCode && baseLoanAmount > 0) {
        const { countyLimitsCache, countyLimitUnavailable } = await fetchCountyLimitsForPricing(
          fetchCountyLimit,
          fipsCode,
          limitFamilies,
        );

        const {
          perFamilyResults,
          anyExceedsStandard,
          allExceedCounty,
        } = evaluateCountyLoanLimits(
          baseLoanAmount,
          numberOfUnits,
          limitFamilies,
          countyLimitsCache,
          countyLimitUnavailable,
          fipsCode,
        );

        let hasGovernmentExceed = false;
        let hasConventionalExceed = false;
        const limitMessages = [];

        for (const result of perFamilyResults) {
          if (result.message) limitMessages.push(result.message);
          if (result.exceedsStandardLimit) {
            if (['FHA', 'VA', 'USDA'].includes(result.family)) hasGovernmentExceed = true;
            else hasConventionalExceed = true;
          }
        }

        // Always set loanLimitInfo so county limit badges display on product cards
        const worstType = perFamilyResults.some(r => r.warningType === 'error') ? 'error' :
                          perFamilyResults.some(r => r.warningType === 'warning') ? 'warning' : null;
        if (limitMessages.length > 0) {
          setLoanLimitWarning(limitMessages.join('\n'));
        }
        setLoanLimitInfo({ warningType: worstType, perFamily: perFamilyResults });

        // Block pricing only if ALL selected families exceed their county limit
        if (allExceedCounty && limitFamilies.length > 0 && perFamilyResults.some(r => r.exceedsCountyLimit)) {
          setIsRunning(false);
          return;
        }

        // Auto-inject subfamilies if any family exceeds its standard limit
        if (anyExceedsStandard) {
          const subFamilySet = new Set();
          if (hasGovernmentExceed) subFamilySet.add('High Balance');
          if (hasConventionalExceed) {
            subFamilySet.add('High Balance');
            subFamilySet.add('Super Conforming');
          }
          subFamilies = [...subFamilySet];
        }
      }

      const pricingFamilies = families ?? activeFamilies;
      const request = buildPricingRequestFromLoanDetail({
        productFamilies: pricingFamilies,
        productSubFamilies: subFamilies.length > 0 ? subFamilies : [],
        terms: mergeTermsForNonQmPricing(
          selectedTerms.length > 0 ? selectedTerms : [],
          pricingFamilies,
        ),
        productTypes: selectedAmortTypes.length > 0 ? selectedAmortTypes : [],
      });
      if (!request) {
        setPricingError('Pricing request is unavailable for this loan.');
        setIsRunning(false);
        return;
      }

      const fico = applyRepresentativeFicoToPricingLoan(loanDetail, request.loan);
      if (fico == null) {
        setPricingError(
          'A valid credit score (300–850) is required to run pricing. Enter credit scores on the Credit tab, save the loan, then try again.',
        );
        setIsRunning(false);
        return;
      }

      setLastPricingRequest(request);

      const response = await runPricingWithEngine({
        featureFlags,
        pricingRequest: request,
        loanId,
        runPricing,
        runLoanPricing,
      });
      const nextProducts = response.products || [];
      setProducts(nextProducts);
      const quotedAtNext = response.quotedAt || new Date().toISOString();
      setQuotedAt(quotedAtNext);
      setRateSheetDate(response.rateSheetId || null);
      const snap = { ...request, loan: { ...request.loan } };
      delete snap.loan.submissionDate;
      setPricingSnapshot(JSON.stringify(snap));

      // Lock-flow convenience: keep the same selected rate if available.
      // If not present in fresh pricing results, require manual rate selection.
      if (preserveSelection && nextProducts.length > 0) {
        const preferredProductShortName = previousSelectedRate?.productShortName
          || initialFilters?.preferredProductShortName
          || assignedProduct;
        const preferredRate = previousSelectedRate?.rate
          ?? initialFilters?.preferredRate
          ?? loanDetail?.loan?.noteRate;
        const targetProduct = nextProducts.find((p) =>
          p.productShortName === preferredProductShortName
          || p.productId === previousSelectedRate?.productId);
        if (targetProduct) {
          const matchedRate = Number.isFinite(Number(preferredRate))
            ? (targetProduct.prices || []).find((rp) => Math.abs(Number(rp.rate) - Number(preferredRate)) < 0.0005)
            : null;
          if (matchedRate) {
            const chosenRate = matchedRate;
            const currentLoanAmount = loanDetail?.loan?.loanAmount || 0;
            const monthlyPayment = targetProduct.paymentType === 'Interest Only'
              ? calcMonthlyIO(chosenRate.rate, currentLoanAmount)
              : calcMonthlyPI(chosenRate.rate, currentLoanAmount, targetProduct.term);

            setSelectedRate({
              quoteId: chosenRate.quoteId,
              productId: targetProduct.productId,
              productShortName: targetProduct.productShortName,
              productName: targetProduct.productName,
              family: targetProduct.family,
              subfamily: targetProduct.subfamily,
              typeName: targetProduct.typeName,
              paymentType: targetProduct.paymentType,
              rate: chosenRate.rate,
              basePrice: chosenRate.basePrice,
              adjustedPrice: chosenRate.adjustedPrice,
              monthlyPayment,
              isIO: targetProduct.paymentType === 'Interest Only',
              term: targetProduct.term,
              adjustments: targetProduct.adjustments,
              totalAdjustment: targetProduct.totalAdjustment,
            });
            setAssignError(null);
          } else {
            setSelectedRate(null);
            setAssignedQuoteId(null);
            setAssignSuccess(null);
            setAssignError('The previously selected rate is no longer available. Please select a new rate before applying reprice.');
          }
        }
      }
    } catch (err) {
      console.error('[PricingTab] pricing error:', err);
      // If pricing fails, do not leave lock flow actionable.
      setSelectedRate(null);
      setAssignedQuoteId(null);
      setAssignSuccess(null);
      // Parse structured errors from pricing engine for clean display
      const errData = err?.data;
      if (errData?.errors && typeof errData.errors === 'object') {
        // Validation-style errors: { errors: { field: [msgs] } }
        const lines = [];
        for (const [field, msgs] of Object.entries(errData.errors)) {
          const msgList = Array.isArray(msgs) ? msgs : [msgs];
          msgList.forEach(m => lines.push(`${field}: ${m}`));
        }
        setPricingError(lines);
      } else {
        setPricingError(getAxiosErrorMessage(err, 'Failed to fetch pricing.'));
      }
    } finally {
      setIsRunning(false);
    }
  }, [loanDetail, selectedFamilies, selectedTerms, selectedAmortTypes, runPricing, runLoanPricing, featureFlags, selectedRate, assignedProduct, buildPricingRequestFromLoanDetail, loanId]);

  // ── Auto-run pricing when triggered (import modal or sidebar button) ──
  const autoRunFired = useRef(false);
  useEffect(() => {
    if (!autoRun) { autoRunFired.current = false; return; }
    if (autoRun && loanDetail && !autoRunFired.current && !isRunning) {
      autoRunFired.current = true;
      const hasPreferred = initialFilters?.preferredProductShortName && initialFilters?.preferredRate != null;
      handleRunPricing({
        preserveSelection: hasPreferred,
        previousSelectedRate: hasPreferred ? {
          productShortName: initialFilters.preferredProductShortName,
          rate: initialFilters.preferredRate,
        } : null,
      });
      onAutoRunComplete?.();
    }
  }, [autoRun, loanDetail, handleRunPricing, onAutoRunComplete, isRunning, initialFilters]);

  // Auto-run pricing once when entering lock mode from the top card.
  useEffect(() => {
    if (!lockModePendingRunRef.current) return;
    if (isRunning) return;

    lockModePendingRunRef.current = false;
    const preserveSelection = lockModePreserveSelectionRef.current;
    lockModePreserveSelectionRef.current = false;
    handleRunPricing({ preserveSelection });
  }, [isRunning, handleRunPricing]);

  // After lock-mode initiated pricing completes, scroll to lock panel.
  // scroll to the selected rate / lock panel like normal pricing flow.
  const prevIsRunningRef = useRef(false);
  useEffect(() => {
    const wasRunning = prevIsRunningRef.current;
    prevIsRunningRef.current = isRunning;

    if (!lockModeScrollAfterRunRef.current) return;

    const pricingJustFinished = wasRunning && !isRunning;
    if (!pricingJustFinished) return;

    // One-shot behavior for each lock flow entry.
    lockModeScrollAfterRunRef.current = false;
    if (!products?.length) return;
    if (!selectedRate || !assignedQuoteId) return;

    const timer = setTimeout(() => {
      selectedRateDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);

    return () => clearTimeout(timer);
  }, [isRunning, products, selectedRate, assignedQuoteId]);

  // ── Stale pricing detection ──
  const isPricingStale = useMemo(() => {
    if (!pricingSnapshot || products.length === 0) return false;
    try {
      const req = buildPricingRequestFromLoanDetail({
        productFamilies: selectedFamilies.length > 0 ? [...selectedFamilies] : [],
        terms: selectedTerms.length > 0 ? selectedTerms : [],
        productTypes: selectedAmortTypes.length > 0 ? selectedAmortTypes : [],
      });
      if (!req) return false;
      delete req.loan?.submissionDate;
      return JSON.stringify(req) !== pricingSnapshot;
    } catch { return false; }
  }, [pricingSnapshot, products, loanDetail, lockDuration, compensationType, selectedFamilies,
      selectedTerms, selectedAmortTypes, buildPricingRequestFromLoanDetail]);

  const loanAmount = loanDetail?.loan?.loanAmount || 0;

  // ── Filter & sort products ──
  const filteredProducts = useMemo(() => {
    let list = eligibleOnly
      ? products.filter(p => p.isEligible)
      : [...products].sort((a, b) => (b.isEligible ? 1 : 0) - (a.isEligible ? 1 : 0));

    if (isReadOnlyUnlocked && assignedProduct) {
      list = list.filter((p) => p.productShortName === assignedProduct);
    }

    if (sortBy === 'price') {
      list = [...list].sort((a, b) => {
        const aPrice = a.parRate?.adjustedPrice ?? -Infinity;
        const bPrice = b.parRate?.adjustedPrice ?? -Infinity;
        return bPrice - aPrice;
      });
    } else if (sortBy === 'payment') {
      const getPayment = (p) => {
        if (!p.parRate) return Infinity;
        return p.paymentType === 'Interest Only'
          ? (loanAmount * (p.parRate.rate / 100) / 12)
          : (() => { const r = p.parRate.rate / 100 / 12; const n = p.term * 12; return r === 0 ? loanAmount / n : (loanAmount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1); })();
      };
      list = [...list].sort((a, b) => getPayment(a) - getPayment(b));
    }
    return list;
  }, [products, eligibleOnly, sortBy, loanAmount, isReadOnlyUnlocked, assignedProduct]);

  const toggleFilter = (arr, setArr, val) => {
    setArr(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  };

  // ── Monthly payment estimates ──
  const calcMonthlyPI = (rate, amount, termYears) => {
    if (!rate || !amount || !termYears) return null;
    const r = rate / 100 / 12;
    const n = termYears * 12;
    if (r === 0) return amount / n;
    return (amount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  };
  const calcMonthlyIO = (rate, amount) => {
    if (!rate || !amount) return null;
    return amount * (rate / 100) / 12;
  };

  // ── BPC max LPC enforcement ──
  // BPC cannot exceed the LPC amount from the active compensation schedule
  const bpcExceedsMaxLpc = (() => {
    if (compensationType !== 'BPC' || !selectedRate || !bpcValue || !activeCompensation) return false;
    let maxLpcDollars = (activeCompensation.percentage || 0) / 100 * loanAmount;
    if (activeCompensation.minDollarAmount != null && maxLpcDollars < activeCompensation.minDollarAmount)
      maxLpcDollars = activeCompensation.minDollarAmount;
    if (activeCompensation.maxDollarAmount != null && maxLpcDollars > activeCompensation.maxDollarAmount)
      maxLpcDollars = activeCompensation.maxDollarAmount;
    const bpcDollars = bpcCalcMethod === 'PercentOfLoan'
      ? (loanAmount * (parseFloat(bpcValue) || 0) / 100)
      : (parseFloat(bpcValue) || 0);
    return bpcDollars > 0 && bpcDollars > maxLpcDollars;
  })();

  return (
    <Box>
      {!isLockDeskEnabled && !isLoanLocked && <LockDeskClosedAlert lockDeskStatus={lockDeskStatus} />}

      {noActiveComp && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">No Active Compensation</Typography>
          <Typography variant="body2">There is no active compensation schedule for your company. LPC compensation adjustments will not be applied to pricing results. Contact your broker admin to set up a compensation schedule.</Typography>
        </Alert>
      )}

      {hasUnsavedChanges && (
        <Alert severity="warning" sx={{ mb: 2 }}><strong>Unsaved changes detected.</strong> Save your loan edits before running pricing or locking a rate — pricing uses the saved loan data from the LOS.</Alert>
      )}

      {showAdvisorQuickActions && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Illustrative pricing recommendation — not an underwriting approval or AUS finding.
            Use the actions above or assign/lock from this tab when you are ready.
          </Typography>
        </Alert>
      )}

      {/* Run Pricing Controls */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardHeader
          title={<SectionHeader title="Pricing Engine" icon={<CalculateIcon fontSize="small" />} sx={{ mb: 0 }} />}
          action={
            <Button size="small" variant="text" startIcon={<FilterListIcon />} onClick={() => setShowFilters(!showFilters)} sx={{ color: showFilters ? 'primary.main' : 'text.secondary' }}>
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          }
        />
        <CardContent>
          {activeCompensation && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, fontSize: '0.75rem' }}>
              Active Comp: <strong>{activeCompensation.percentage}%</strong>{activeCompensation.minDollarAmount != null || activeCompensation.maxDollarAmount != null
                ? <> (${(activeCompensation.minDollarAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}–${(activeCompensation.maxDollarAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})</>
                : ''}
            </Typography>
          )}
          <Stack direction="row" spacing={2} alignItems="flex-end" flexWrap="wrap">
            <TextField select label="Compensation" size="small" value={compensationType} onChange={(e) => { setCompensationType(e.target.value); if (e.target.value === 'LPC') { setBpcValue(''); } }} sx={{ width: 130 }}>
              <MenuItem value="LPC">LPC</MenuItem>
              <MenuItem value="BPC">BPC</MenuItem>
            </TextField>
            {compensationType === 'BPC' && (
              <>
                <TextField select label="Calc Method" size="small" value={bpcCalcMethod} onChange={(e) => { setBpcCalcMethod(e.target.value); setBpcValue(''); }} sx={{ width: 150 }}>
                  <MenuItem value="PercentOfLoan">Percent</MenuItem>
                  <MenuItem value="FixedAmount">Fixed Amount</MenuItem>
                </TextField>
                {bpcCalcMethod === 'FixedAmount' ? (
                  <Box sx={{ width: 150 }}><CurrencyInput label="BPC $" size="small" fullWidth value={bpcValue} onChange={setBpcValue} placeholder="e.g. 5,000" /></Box>
                ) : (
                  <TextField label="BPC %" size="small" type="number" slotProps={{ htmlInput: { step: 0.125, min: 0 } }} value={bpcValue} onChange={(e) => setBpcValue(e.target.value)} placeholder="e.g. 1.5" sx={{ width: 150 }} />
                )}
              </>
            )}
            <Button variant="contained" onClick={handleRunPricing} disabled={isRunning || hasUnsavedChanges} startIcon={isRunning ? <CircularProgress size={16} /> : <CalculateIcon />} title={hasUnsavedChanges ? 'Save your changes before running pricing' : ''}>
              {isRunning ? 'Running...' : 'Run Pricing'}
            </Button>
            {products.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {quotedAt ? new Date(quotedAt).toLocaleString() : '—'}
                {rateSheetDate && <> | Rates Updated: {new Date(rateSheetDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} {new Date(rateSheetDate).toLocaleTimeString()}</>}
                {' | '}{filteredProducts.length} products
              </Typography>
            )}
          </Stack>
          {isReadOnlyUnlocked && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Read-only mode: repricing is limited to the assigned product. You can select any rate, but cannot change products.
            </Alert>
          )}
          {isReadOnlyUnlocked && !assignedProduct && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              This loan is read-only and has no assigned product, so repricing is unavailable.
            </Alert>
          )}

          {/* Filters Section */}
          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'background.default', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <FilterListIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary">Filters</Typography>
                <Box sx={{ flex: 1 }} />
                <Button size="small" variant="text" startIcon={<CloseIcon />} onClick={() => { setSelectedFamilies([]); setSelectedTerms([]); }} sx={{ fontSize: '0.7rem' }}>Clear All</Button>
              </Stack>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                {!isReadOnlyUnlocked && (
                  <Box>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Product Families</Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {PRODUCT_FAMILIES.map(pf => (
                        <Chip key={pf.id} label={pf.label} size="small" color={selectedFamilies.includes(pf.id) ? 'primary' : 'default'} variant={selectedFamilies.includes(pf.id) ? 'filled' : 'outlined'} onClick={() => toggleFilter(selectedFamilies, setSelectedFamilies, pf.id)} />
                      ))}
                    </Stack>
                  </Box>
                )}
                {!isReadOnlyUnlocked && (
                  <Box>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Loan Terms</Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {loanTermOptions.map(lt => (
                        <Chip key={lt} label={`${lt}yr`} size="small" color={selectedTerms.includes(lt) ? 'primary' : 'default'} variant={selectedTerms.includes(lt) ? 'filled' : 'outlined'} onClick={() => toggleFilter(selectedTerms, setSelectedTerms, lt)} />
                      ))}
                    </Stack>
                  </Box>
                )}
                {!isReadOnlyUnlocked && (
                  <Box>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Amortization</Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {AMORTIZATION_TYPES.map(at => (
                        <Chip key={at.id} label={at.label} size="small" color={selectedAmortTypes.includes(at.id) ? 'primary' : 'default'} variant={selectedAmortTypes.includes(at.id) ? 'filled' : 'outlined'} onClick={() => toggleFilter(selectedAmortTypes, setSelectedAmortTypes, at.id)} />
                      ))}
                    </Stack>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Lock Term</Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {LOCK_DURATIONS.map(d => (
                      <Chip key={d} label={`${d}d`} size="small" color={lockDuration === d ? 'primary' : 'default'} variant={lockDuration === d ? 'filled' : 'outlined'} onClick={() => setLockDuration(d)} />
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Collapse>

        </CardContent>
      </Card>

      {/* Loan limit errors */}
      {loanLimitInfo?.perFamily?.filter(r => r.warningType === 'error').length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Loan Amount Exceeds County Limit</Typography>
          {loanLimitInfo.perFamily.filter(r => r.warningType === 'error').map((r, i) => <Typography key={i} variant="body2">{r.message}</Typography>)}
        </Alert>
      )}
      {loanLimitInfo?.perFamily?.filter(r => r.warningType === 'warning').length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">High-Cost Area</Typography>
          {loanLimitInfo.perFamily.filter(r => r.warningType === 'warning').map((r, i) => <Typography key={i} variant="body2">{r.message}</Typography>)}
        </Alert>
      )}

      {pricingError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Pricing Engine Error</Typography>
          {Array.isArray(pricingError) ? pricingError.map((line, i) => <Typography key={i} variant="body2">• {line}</Typography>) : <Typography variant="body2">{pricingError}</Typography>}
        </Alert>
      )}

      {assignError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAssignError(null)}>
          {assignError}
        </Alert>
      )}

      {isPricingStale && (
        <Alert severity="warning" sx={{ mb: 2 }} action={<Button size="small" color="warning" startIcon={<RefreshIcon />} onClick={handleRunPricing} disabled={isRunning || hasUnsavedChanges}>Re-run Pricing</Button>}>
          <strong>Pricing inputs have changed.</strong> The prices shown may no longer be accurate. Re-run pricing to get updated results.
        </Alert>
      )}

      {/* Loading */}
      {isRunning && (
        <Card variant="outlined" sx={{ mb: 2 }}><CardContent sx={{ textAlign: 'center', py: 5 }}><CircularProgress sx={{ mb: 2 }} /><Typography variant="h6">Running Pricing Engine...</Typography><Typography variant="body2" color="text.secondary">Fetching product pricing. This typically takes a few seconds.</Typography></CardContent></Card>
      )}

      {/* Empty State */}
      {!isRunning && products.length === 0 && !pricingError && !selectedRate && (
        <Card variant="outlined"><CardContent sx={{ textAlign: 'center', py: 5 }}>
          <CalculateIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">No pricing results</Typography>
          <Typography variant="body2" color="text.secondary">Configure your options and click "Run Pricing" to fetch products and rates from the pricing engine.</Typography>
        </CardContent></Card>
      )}

      {/* Lock Mode Panel */}
      {!isRunning && products.length === 0 && selectedRate && assignedQuoteId && (
        <Grid container justifyContent="center"><Grid size={{ xs: 12, lg: 6 }}>
          <Card variant="outlined" sx={{ border: 2, borderColor: 'primary.main', position: 'sticky', top: 16 }}>
            <CardHeader avatar={<LockIcon color="primary" />} title={<SectionHeader title="Lock Rate" icon={<LockIcon fontSize="small" />} sx={{ mb: 0 }} />} />
            <CardContent>
              <Grid container spacing={2} sx={{ textAlign: 'center', mb: 2 }}>
                <Grid size={4}><Typography variant="caption" color="text.secondary">Rate</Typography><Typography variant="h5">{fmtPct(selectedRate.rate)}</Typography></Grid>
                <Grid size={4}><Typography variant="caption" color="text.secondary">Price</Typography><Typography variant="h5" color={selectedRate.adjustedPrice >= 100 ? 'success.main' : 'error.main'}>{fmt(selectedRate.adjustedPrice, 3)}</Typography></Grid>
                <Grid size={4}><Typography variant="caption" color="text.secondary">Lock Term</Typography><Typography variant="h5">{lockDuration} days</Typography></Grid>
              </Grid>
              <Typography variant="caption" color="text.secondary">Program Details</Typography>
              <Table size="small" sx={{ mb: 2, '& td': { py: 0.3, fontSize: '0.8rem' } }}>
                <TableBody>
                  <TableRow><TableCell><Typography variant="body2" color="text.secondary">Program</Typography></TableCell><TableCell align="right"><Typography variant="body2" fontWeight={700}>{selectedRate.productName}</Typography></TableCell></TableRow>
                  {selectedRate.family && <TableRow><TableCell><Typography variant="body2" color="text.secondary">Type</Typography></TableCell><TableCell align="right"><Typography variant="body2" fontWeight={700}>{selectedRate.family}</Typography></TableCell></TableRow>}
                  {selectedRate.typeName && <TableRow><TableCell><Typography variant="body2" color="text.secondary">Amortization</Typography></TableCell><TableCell align="right"><Typography variant="body2" fontWeight={700}>{selectedRate.typeName}</Typography></TableCell></TableRow>}
                  <TableRow><TableCell><Typography variant="body2" color="text.secondary">Term</Typography></TableCell><TableCell align="right"><Typography variant="body2" fontWeight={700}>{selectedRate.term}yr</Typography></TableCell></TableRow>
                </TableBody>
              </Table>
              {assignSuccess && <Alert severity="success" sx={{ mb: 2 }}>{assignSuccess}</Alert>}
              {isLoanLocked && <Alert severity="info" sx={{ mb: 2 }}><strong>Rate is locked</strong>{loan.lockExpirationDate && ` until ${new Date(loan.lockExpirationDate).toLocaleDateString()}`}</Alert>}
              {!isLoanLocked && (
                <Button variant="contained" color="success" fullWidth sx={{ mb: 2 }} disabled={isLocking || !isLockDeskEnabled} startIcon={isLocking ? <CircularProgress size={16} /> : <LockIcon />} onClick={handleLockRate} title={!isLockDeskEnabled ? 'The Lock Desk is currently closed' : ''}>
                  {!isLockDeskEnabled ? 'Lock Desk Closed' : isLocking ? 'Locking Rate...' : 'Lock Rate'}
                </Button>
              )}
              <Box sx={{ textAlign: 'center' }}><Button size="small" variant="text" startIcon={<CalculateIcon />} onClick={() => { setSelectedRate(null); setAssignSuccess(null); setAssignedQuoteId(null); }}>Run Pricing Instead</Button></Box>
            </CardContent>
          </Card>
        </Grid></Grid>
      )}

      {ToastContainer}
      {ConfirmModalContainer}

      {/* Product Comparison Cards */}
      {!isRunning && products.length > 0 && (
        <>
          {/* Toggle eligible + counts */}
          {(() => {
            const eligibleCount = products.filter(p => p.isEligible).length;
            const ineligibleCount = products.length - eligibleCount;
            return (
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={700}>{filteredProducts.length} Product{filteredProducts.length !== 1 ? 's' : ''}</Typography>
                  {!eligibleOnly && ineligibleCount > 0 && (
                    <Stack direction="row" spacing={0.5}>
                      <StatusBadge variant="success">{eligibleCount} eligible</StatusBadge>
                      <StatusBadge variant="danger">{ineligibleCount} ineligible</StatusBadge>
                    </Stack>
                  )}
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField select size="small" label="Sort by" value={sortBy} onChange={e => setSortBy(e.target.value)} sx={{ width: 150 }}>
                    <MenuItem value="price">Best Price</MenuItem>
                    <MenuItem value="payment">Lowest Payment</MenuItem>
                  </TextField>
                  <FormControlLabel control={<Switch size="small" checked={eligibleOnly} onChange={(e) => setEligibleOnly(e.target.checked)} />} label={<Typography variant="body2">Eligible only</Typography>} />
                </Stack>
              </Stack>
            );
          })()}

          {/* Ineligibility summary bar */}
          {!eligibleOnly && (() => {
            const ineligibleProducts = products.filter(p => !p.isEligible && p.eligibilityAnalysis?.failingParams?.length > 0);
            if (ineligibleProducts.length === 0) return null;
            const categoryCounts = {};
            for (const p of ineligibleProducts) {
              const seenParams = new Set();
              for (const fp of p.eligibilityAnalysis.failingParams) {
                if (isLenderIdIneligibleParam(fp.param)) continue;
                if (!seenParams.has(fp.param)) {
                  seenParams.add(fp.param);
                  if (!categoryCounts[fp.param]) {
                    categoryCounts[fp.param] = { count: 0, label: fp.label, icon: fp.icon, changeable: fp.changeable };
                  }
                  categoryCounts[fp.param].count++;
                }
              }
            }
            const sorted = Object.entries(categoryCounts)
              .sort(([, a], [, b]) => b.count - a.count || (a.changeable ? -1 : 1))
              .slice(0, 8);

            return (
              <Card variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
                <CardContent sx={{ py: 1, px: 2 }}>
                  <Typography variant="caption" fontWeight={700} sx={{ mb: 0.5, display: 'block' }}>Top ineligibility reasons across {ineligibleProducts.length} products</Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {sorted.map(([cat, info]) => (
                      <Chip key={cat} label={`${info.label} (${info.count})`} size="small" color={info.changeable ? 'warning' : 'error'} variant="outlined" title={`${info.label}: blocks ${info.count} product${info.count !== 1 ? 's' : ''}${info.changeable ? ' (adjustable)' : ' (fixed)'}`} />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            );
          })()}

          {/* Product Cards — when a rate is selected, show only the chosen product + detail side panel */}
          {(() => {
            // Find the selected product for the side-by-side layout
            const selectedProduct = selectedRate
              ? filteredProducts.find(p => p.productShortName === selectedRate.productShortName)
              : null;

            // Helper to render rate grid for a product
            const RATES_AROUND_PAR = 5;
            const renderRateGrid = (p) => {
              // Sort by adjustedPrice descending (highest/best prices on top)
              const sorted = [...p.prices].sort((a, b) => b.adjustedPrice - a.adjustedPrice);

              // Find par index in the sorted array
              let parIdx = 0;
              let minDiff = Math.abs(sorted[0]?.adjustedPrice - PAR_PRICE_TARGET);
              sorted.forEach((rp, i) => {
                const diff = Math.abs(rp.adjustedPrice - PAR_PRICE_TARGET);
                if (diff < minDiff) { minDiff = diff; parIdx = i; }
              });

              // Constrain to RATES_AROUND_PAR rows above and below par
              const startIdx = Math.max(0, parIdx - RATES_AROUND_PAR);
              const endIdx = Math.min(sorted.length, parIdx + RATES_AROUND_PAR + 1);
              const visibleRates = showAllRates ? sorted : sorted.slice(startIdx, endIdx);
              const hiddenCount = sorted.length - (endIdx - startIdx);

              return (
                <Box sx={{ overflow: 'hidden' }}>
                  {p.adjustments.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" fontWeight={700} sx={{ mb: 0.5, display: 'block' }}>Price Adjustments</Typography>
                      <Table size="small" sx={{ fontSize: '0.75rem', mb: 1 }}>
                        <TableBody>
                          {p.adjustments.map((adj, i) => (
                            <TableRow key={i}><TableCell sx={{ py: 0.3 }}>{adj.description}</TableCell><TableCell align="right" sx={{ py: 0.3 }}><Typography variant="caption" fontWeight={700} color={adj.points > 0 ? 'error.main' : adj.points < 0 ? 'success.main' : undefined}>{adj.points > 0 ? '-' : adj.points < 0 ? '+' : ''}{fmt(Math.abs(adj.points), 3)}</Typography></TableCell></TableRow>
                          ))}
                          <TableRow sx={{ bgcolor: 'action.hover' }}><TableCell sx={{ py: 0.3 }}><Typography variant="caption" fontWeight={700}>Total</Typography></TableCell><TableCell align="right" sx={{ py: 0.3 }}><Typography variant="caption" fontWeight={700} color={p.totalAdjustment > 0 ? 'error.main' : p.totalAdjustment < 0 ? 'success.main' : undefined}>{p.totalAdjustment > 0 ? '-' : p.totalAdjustment < 0 ? '+' : ''}{fmt(Math.abs(p.totalAdjustment), 3)}</Typography></TableCell></TableRow>
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                  <Box sx={{ maxHeight: 450, overflowY: 'auto', overflowX: 'auto' }}>
                    <Table size="small" sx={{ fontSize: '0.8rem' }}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell>Rate</TableCell>
                          <TableCell align="right">Base Price</TableCell>
                          <TableCell align="right">Adj. Price</TableCell>
                          <TableCell align="right">{p.paymentType === 'Interest Only' ? 'Est. IO' : 'Est. P&I'}</TableCell>
                          <TableCell sx={{ width: 52 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {!showAllRates && startIdx > 0 && (
                          <TableRow><TableCell colSpan={5} align="center"><Button size="small" variant="text" onClick={() => setShowAllRates(true)} sx={{ fontSize: '0.7rem' }}>{startIdx} more {startIdx === 1 ? 'rate' : 'rates'} with higher price (lender credits)</Button></TableCell></TableRow>
                        )}
                        {visibleRates.map((rp, i) => {
                          const origIdx = sorted.indexOf(rp);
                          const isPar = origIdx === parIdx;
                          const pi = p.paymentType === 'Interest Only' ? calcMonthlyIO(rp.rate, loanAmount) : calcMonthlyPI(rp.rate, loanAmount, p.term);
                          const isRateSelected = selectedRate?.productShortName === p.productShortName && selectedRate?.rate === rp.rate;
                          const rateSelectable = !isLoanLocked;
                          return (
                            <TableRow
                              key={i}
                              hover
                              selected={isRateSelected}
                              sx={{
                                cursor: rateSelectable ? 'pointer' : 'not-allowed',
                                opacity: rateSelectable ? 1 : 0.45,
                                ...(isPar ? { bgcolor: 'rgba(var(--mui-palette-warning-mainChannel) / 0.10)' } : {}),
                              }}
                              onClick={() => {
                                if (!rateSelectable) return;
                                setSelectedRate({
                                  quoteId: rp.quoteId,
                                  productId: p.productId,
                                  productShortName: p.productShortName,
                                  productName: p.productName,
                                  family: p.family,
                                  subfamily: p.subfamily,
                                  typeName: p.typeName,
                                  paymentType: p.paymentType,
                                  rate: rp.rate,
                                  basePrice: rp.basePrice,
                                  adjustedPrice: rp.adjustedPrice,
                                  monthlyPayment: pi,
                                  isIO: p.paymentType === 'Interest Only',
                                  term: p.term,
                                  adjustments: p.adjustments,
                                  totalAdjustment: p.totalAdjustment,
                                });
                                setShowAssignConfirm(false);
                                setAssignError(null);
                                setAssignSuccess(null);
                                setTimeout(() => selectedRateDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
                              }}
                            >
                              <TableCell><Typography variant="body2" fontWeight={700}>{fmtPct(rp.rate)}</Typography></TableCell>
                              <TableCell align="right">{fmt(rp.basePrice, 3)}</TableCell>
                              <TableCell align="right"><Typography variant="body2" fontWeight={700} color={rp.adjustedPrice >= 100 ? 'success.main' : 'error.main'}>{fmt(rp.adjustedPrice, 3)}</Typography></TableCell>
                              <TableCell align="right">{pi ? fmtDollar(pi) : '—'}</TableCell>
                              <TableCell align="center" sx={{ width: 52 }}>{isPar && <NearParBadge />}</TableCell>
                            </TableRow>
                          );
                        })}
                        {!showAllRates && endIdx < sorted.length && (
                          <TableRow><TableCell colSpan={5} align="center"><Button size="small" variant="text" onClick={() => setShowAllRates(true)} sx={{ fontSize: '0.7rem' }}>{sorted.length - endIdx} more {sorted.length - endIdx === 1 ? 'rate' : 'rates'} with lower price (discount points)</Button></TableCell></TableRow>
                        )}
                        {showAllRates && hiddenCount > 0 && (
                          <TableRow><TableCell colSpan={5} align="center"><Button size="small" variant="text" onClick={() => setShowAllRates(false)} sx={{ fontSize: '0.7rem' }}>Show fewer rates</Button></TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              );
            };

            // ── Always show product cards in their original grid ──
            return (
              <>
                <Grid container spacing={2}>
                  {filteredProducts.map((p) => {
                    const isExpanded = expandedProducts.has(p.productShortName);
                    const parRateInfo = p.parRate;
                    const isIO = p.paymentType === 'Interest Only';
                    const monthlyPayment = parRateInfo ? (isIO ? calcMonthlyIO(parRateInfo.rate, loanAmount) : calcMonthlyPI(parRateInfo.rate, loanAmount, p.term)) : null;
                    const familyLimit = loanLimitInfo?.perFamily?.find(r => r.family === p.family)
                      || ((['VA', 'USDA'].includes(p.family)) ? loanLimitInfo?.perFamily?.find(r => r.family === 'FHA') : null);
                    const isSelectedProduct = selectedRate?.productShortName === p.productShortName;
                    const displayIneligibleReasons = filterIneligibleReasonStrings(p.ineligibleReasons || []);

                    return (
                      <Grid size={{ xs: 12, md: 6, lg: 4 }} key={p.productShortName}>
                        <Card variant="outlined" sx={{ opacity: p.isEligible ? 1 : 0.85, overflow: 'hidden', ...(!p.isEligible ? { borderColor: 'error.main' } : isSelectedProduct ? { borderColor: 'primary.main', borderWidth: 2 } : assignedProduct && p.productShortName === assignedProduct ? { borderColor: 'warning.main', borderWidth: 2, boxShadow: '0 0 8px rgba(249,168,37,0.25)' } : {}) }}>
                          <CardHeader
                            title={
                              <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{p.productName}</Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                  <Chip label={p.family} size="small" color="primary" variant="outlined" />
                                  <Chip label={`${p.term}yr ${p.typeName}`} size="small" color="info" variant="outlined" />
                                  {p.paymentType && <Chip label={p.paymentType} size="small" variant="outlined" />}
                                  {familyLimit && familyLimit.countyLimitAmount > 0 && (
                                    <Chip label={`County Limit: ${fmtDollar(familyLimit.countyLimitAmount)}`} size="small" color={familyLimit.warningType === 'error' ? 'error' : familyLimit.warningType === 'warning' ? 'warning' : 'success'} variant="outlined" />
                                  )}
                                </Stack>
                              </Box>
                            }
                            action={
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                {assignedProduct && p.productShortName === assignedProduct && (
                                  <Chip label="Assigned" size="small" color="warning" sx={{ fontWeight: 600 }} />
                                )}
                                <StatusBadge status={p.isEligible ? 'Eligible' : 'Ineligible'} map={ELIGIBILITY_MAP} />
                              </Stack>
                            }
                          />
                          <CardContent>
                            {p.isEligible && parRateInfo ? (
                              <>
                                <Grid container spacing={1} sx={{ textAlign: 'center', mb: 2 }}>
                                  <Grid size={4}><Box><NearParRateLabel /><Typography variant="h5">{fmtPct(parRateInfo.rate)}</Typography></Box></Grid>
                                  <Grid size={4}><Typography variant="caption" color="text.secondary">Price</Typography><Typography variant="h5">{fmt(parRateInfo.adjustedPrice, 3)}</Typography></Grid>
                                  <Grid size={4}><Typography variant="caption" color="text.secondary">{isIO ? 'Est. IO' : 'Est. P&I'}</Typography><Typography variant="h5">{monthlyPayment ? fmtDollar(monthlyPayment) : '—'}</Typography></Grid>
                                </Grid>
                                {p.adjustments.length > 0 && (
                                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                                    <Typography variant="caption" color="text.secondary">Total Adjustment {compensationType === 'LPC' && <Typography component="span" variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>(incl. compensation)</Typography>}</Typography>
                                    <Typography variant="caption" fontWeight={700} color={p.totalAdjustment > 0 ? 'error.main' : p.totalAdjustment < 0 ? 'success.main' : undefined}>
                                      {p.totalAdjustment > 0 ? '-' : p.totalAdjustment < 0 ? '+' : ''}{fmt(Math.abs(p.totalAdjustment), 3)} pts
                                    </Typography>
                                  </Stack>
                                )}
                                <Button size="small" variant="outlined" fullWidth startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />} onClick={() => { setExpandedProducts(prev => { const next = new Set(prev); if (isExpanded) next.delete(p.productShortName); else next.add(p.productShortName); return next; }); }}>
                                  {isExpanded ? 'Hide Rate Grid' : `View Rate Grid (${p.prices.length} rates)`}
                                </Button>
                              </>
                            ) : (
                              <Box>
                                {hasDisplayableEligibilityAnalysis(p.eligibilityAnalysis) ? (
                                  <IneligibleReasonsPanel analysis={p.eligibilityAnalysis} />
                                ) : displayIneligibleReasons.length > 0 ? (
                                  <Box component="ul" sx={{ pl: 2, mb: 0 }}>{displayIneligibleReasons.map((r, i) => <Typography component="li" key={i} variant="caption" color="error.main">{r}</Typography>)}</Box>
                                ) : null}
                                {p.error && <Typography variant="caption" color="error.main">{p.error}</Typography>}
                              </Box>
                            )}
                          </CardContent>
                          {isExpanded && p.isEligible && (
                            <CardContent sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>{renderRateGrid(p)}</CardContent>
                          )}
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>

                {/* Selected rate detail panel */}
                {selectedRate && selectedProduct && (
                  <Box ref={selectedRateDetailRef} sx={{ mt: 2 }}>
                    <Card variant="outlined" sx={{ border: 2, borderColor: 'primary.main' }}>
                      <CardHeader
                        avatar={<CheckIcon color="primary" />}
                        title={<SectionHeader title={`Selected Rate — ${selectedRate.productName}`} sx={{ mb: 0 }} />}
                        action={<Button size="small" variant="text" startIcon={<CloseIcon />} onClick={() => { setSelectedRate(null); setShowAssignConfirm(false); setAssignError(null); setAssignSuccess(null); }}>Close</Button>}
                      />
                      <CardContent>
                        <Grid container spacing={3}>
                          {/* Left column */}
                          <Grid size={{ xs: 12, lg: 6 }}>
                            <Grid container spacing={1} sx={{ textAlign: 'center', mb: 2 }}>
                              <Grid size={4}><Typography variant="caption" color="text.secondary">Rate</Typography><Typography variant="h5">{fmtPct(selectedRate.rate)}</Typography></Grid>
                              <Grid size={4}><Typography variant="caption" color="text.secondary">Price</Typography><Typography variant="h5" color={selectedRate.adjustedPrice >= 100 ? 'success.main' : 'error.main'}>{fmt(selectedRate.adjustedPrice, 3)}</Typography></Grid>
                              <Grid size={4}><Typography variant="caption" color="text.secondary">{selectedRate.isIO ? 'Est. IO' : 'Est. P&I'}</Typography><Typography variant="h5">{selectedRate.monthlyPayment ? fmtDollar(selectedRate.monthlyPayment) : '—'}</Typography></Grid>
                            </Grid>

                            {(() => {
                              const isLpc = compensationType === 'LPC';
                              const adjPrice = selectedRate.adjustedPrice;
                              const priceDelta = adjPrice - 100;
                              const priceDeltaDollars = (priceDelta / 100) * loanAmount;
                              let compPct = 0;
                              let compDollars = 0;
                              if (isLpc && activeCompensation) {
                                compPct = activeCompensation.percentage || 0;
                                compDollars = (compPct / 100) * loanAmount;
                                if (activeCompensation.minDollarAmount != null && compDollars < activeCompensation.minDollarAmount) compDollars = activeCompensation.minDollarAmount;
                                if (activeCompensation.maxDollarAmount != null && compDollars > activeCompensation.maxDollarAmount) compDollars = activeCompensation.maxDollarAmount;
                              } else if (!isLpc && bpcValue) {
                                compDollars = bpcCalcMethod === 'PercentOfLoan' ? (loanAmount * (parseFloat(bpcValue) || 0) / 100) : (parseFloat(bpcValue) || 0);
                                compPct = loanAmount > 0 ? (compDollars / loanAmount) * 100 : 0;
                              }
                              const borrowerCreditOrCost = priceDeltaDollars;
                              let maxLpcDollars = 0;
                              let maxLpcPct = 0;
                              if (activeCompensation) {
                                maxLpcPct = activeCompensation.percentage || 0;
                                maxLpcDollars = (maxLpcPct / 100) * loanAmount;
                                if (activeCompensation.minDollarAmount != null && maxLpcDollars < activeCompensation.minDollarAmount) maxLpcDollars = activeCompensation.minDollarAmount;
                                if (activeCompensation.maxDollarAmount != null && maxLpcDollars > activeCompensation.maxDollarAmount) maxLpcDollars = activeCompensation.maxDollarAmount;
                              }
                              return (
                                <>
                                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Price Breakdown</Typography>
                                  <Table size="small" sx={{ mb: 1, '& td': { py: 0.3, fontSize: '0.8rem' } }}>
                                    <TableBody>
                                      <TableRow><TableCell><Typography variant="body2" color="text.secondary">Base Price</Typography></TableCell><TableCell align="right"><Typography variant="body2" fontWeight={700} color={selectedRate.basePrice >= 100 ? 'success.main' : 'error.main'}>{fmt(selectedRate.basePrice, 3)}</Typography></TableCell></TableRow>
                                      {selectedRate.adjustments?.length > 0 && selectedRate.adjustments.map((adj, i) => (
                                        <TableRow key={i}><TableCell sx={{ pl: 3 }}><Typography variant="caption" color="text.secondary">{adj.description}</Typography></TableCell><TableCell align="right"><Typography variant="caption" color={adj.points > 0 ? 'error.main' : adj.points < 0 ? 'success.main' : undefined}>{adj.points > 0 ? '-' : adj.points < 0 ? '+' : ''}{fmt(Math.abs(adj.points), 3)}</Typography></TableCell></TableRow>
                                      ))}
                                      {selectedRate.totalAdjustment !== 0 && (
                                        <TableRow sx={{ borderTop: 1, borderColor: 'divider' }}><TableCell sx={{ pl: 3 }}><Typography variant="caption" fontWeight={700} color="text.secondary">Total Adjustments {compensationType === 'LPC' && <Typography component="span" variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>(incl. compensation)</Typography>}</Typography></TableCell><TableCell align="right"><Typography variant="caption" fontWeight={700} color={selectedRate.totalAdjustment > 0 ? 'error.main' : 'success.main'}>{selectedRate.totalAdjustment > 0 ? '-' : '+'}{fmt(Math.abs(selectedRate.totalAdjustment), 3)}</Typography></TableCell></TableRow>
                                      )}
                                      <TableRow sx={{ borderTop: 2, borderColor: 'divider' }}><TableCell><Typography variant="body2" fontWeight={700}>Net Price</Typography></TableCell><TableCell align="right"><Typography variant="body2" fontWeight={700} color={adjPrice >= 100 ? 'success.main' : 'error.main'}>{fmt(adjPrice, 3)}</Typography></TableCell></TableRow>
                                    </TableBody>
                                  </Table>

                                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Your Compensation ({compensationType})</Typography>
                                  <Card variant="outlined" sx={{ mb: 1, bgcolor: 'action.hover' }}>
                                    <CardContent sx={{ py: 1, px: 2 }}>
                                      <Stack direction="row" justifyContent="space-between"><Typography variant="caption" color="text.secondary">Comp Rate</Typography><Typography variant="body2" fontWeight={700}>{compPct > 0 ? `${fmt(compPct, 3)}%` : '—'}</Typography></Stack>
                                      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}><Typography variant="caption" color="text.secondary">You Earn</Typography><Typography variant="subtitle1" fontWeight={700} color="success.main">{compDollars > 0 ? fmtDollar(compDollars) : '—'}</Typography></Stack>
                                      {isLpc && activeCompensation && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>LPC — deducted from price by lender{activeCompensation.minDollarAmount != null && activeCompensation.maxDollarAmount != null && ` · Range: $${(activeCompensation.minDollarAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}–$${(activeCompensation.maxDollarAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</Typography>
                                      )}
                                      {!isLpc && bpcValue && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>BPC — paid by borrower at closing (not deducted from price)</Typography>}
                                    </CardContent>
                                  </Card>

                                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Borrower Impact</Typography>
                                  <Card variant="outlined" sx={{ mb: 1, bgcolor: borrowerCreditOrCost >= 0 ? 'rgba(var(--mui-palette-success-mainChannel) / 0.10)' : 'rgba(var(--mui-palette-error-mainChannel) / 0.10)' }}>
                                    <CardContent sx={{ py: 1, px: 2 }}>
                                      {borrowerCreditOrCost >= 0 ? (
                                        <>
                                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="caption">Lender Credit</Typography>
                                            <Typography variant="subtitle1" fontWeight={700} color="success.main">{fmtDollar(borrowerCreditOrCost)}</Typography>
                                          </Stack>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{!isLpc && bpcValue && compDollars > 0 ? `${fmtDollar(borrowerCreditOrCost)} credit toward closing costs (cannot be applied to broker compensation)` : `Price is above the 100.000 par target (${fmt(priceDelta, 3)} pts) — borrower receives a credit toward closing costs`}</Typography>
                                        </>
                                      ) : (
                                        <>
                                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="caption">Discount Points</Typography>
                                            <Typography variant="subtitle1" fontWeight={700} color="error.main">{fmtDollar(Math.abs(borrowerCreditOrCost))}</Typography>
                                          </Stack>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Price is below the 100.000 par target ({fmt(priceDelta, 3)} pts) — borrower pays discount points to buy down the rate</Typography>
                                        </>
                                      )}
                                      {!isLpc && bpcValue && compDollars > 0 && (
                                        <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                                          <Stack direction="row" justifyContent="space-between"><Typography variant="caption" fontWeight={700}>Broker Compensation (BPC)</Typography><Typography variant="body2" fontWeight={700} color="error.main">{fmtDollar(compDollars)}</Typography></Stack>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Paid by borrower at closing</Typography>
                                        </Box>
                                      )}
                                    </CardContent>
                                  </Card>

                                  {!isLpc && bpcExceedsMaxLpc && (
                                    <Alert severity="error" sx={{ mb: 2 }}><strong>BPC exceeds max LPC.</strong> The borrower-paid compensation ({fmtDollar(compDollars)}) cannot exceed the maximum lender-paid compensation of {fmtDollar(maxLpcDollars)} ({fmt(maxLpcPct, 3)}% per your comp schedule).</Alert>
                                  )}
                                </>
                              );
                            })()}
                          </Grid>

                          {/* Right column */}
                          <Grid size={{ xs: 12, lg: 6 }}>
                            <Table size="small" sx={{ mb: 2, '& td': { py: 0.3, fontSize: '0.8rem' } }}>
                              <TableBody>
                                <TableRow><TableCell><Typography variant="body2" color="text.secondary">Lock Term</Typography></TableCell><TableCell align="right"><Typography variant="body2" fontWeight={700}>{lockDuration} days</Typography></TableCell></TableRow>
                                <TableRow><TableCell><Typography variant="body2" color="text.secondary">Lock Expires</Typography></TableCell><TableCell align="right"><Typography variant="body2" fontWeight={700}>{(() => { const exp = new Date(); exp.setDate(exp.getDate() + lockDuration); return exp.toLocaleDateString(); })()}</Typography></TableCell></TableRow>
                                <TableRow><TableCell><Typography variant="body2" color="text.secondary">Loan Amount</Typography></TableCell><TableCell align="right"><Typography variant="body2" fontWeight={700}>{fmtDollar(loanAmount)}</Typography></TableCell></TableRow>
                              </TableBody>
                            </Table>

                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Program Details</Typography>
                            <Table size="small" sx={{ mb: 2, '& td': { py: 0.3, fontSize: '0.8rem' } }}>
                              <TableBody>
                                <TableRow><TableCell><Typography variant="body2" color="text.secondary">Family</Typography></TableCell><TableCell align="right"><Typography variant="body2" fontWeight={700}>{selectedRate.family}</Typography></TableCell></TableRow>
                                {selectedRate.subfamily && <TableRow><TableCell><Typography variant="body2" color="text.secondary">Subfamily</Typography></TableCell><TableCell align="right"><Typography variant="body2" fontWeight={700}>{selectedRate.subfamily}</Typography></TableCell></TableRow>}
                                <TableRow><TableCell><Typography variant="body2" color="text.secondary">Type</Typography></TableCell><TableCell align="right"><Typography variant="body2" fontWeight={700}>{selectedRate.typeName}</Typography></TableCell></TableRow>
                                {selectedRate.paymentType && <TableRow><TableCell><Typography variant="body2" color="text.secondary">Payment</Typography></TableCell><TableCell align="right"><Typography variant="body2" fontWeight={700}>{selectedRate.paymentType}</Typography></TableCell></TableRow>}
                              </TableBody>
                            </Table>

                            {assignSuccess && <Alert severity="success" sx={{ mb: 2 }}>{assignSuccess}</Alert>}
                            {assignError && <Alert severity="error" sx={{ mb: 2 }}>{assignError}</Alert>}

                            {assignSuccess && assignedQuoteId && !isLoanLocked && (
                              <Button variant="contained" color="success" fullWidth sx={{ mb: 2 }} disabled={isLocking || !isLockDeskEnabled} startIcon={isLocking ? <CircularProgress size={16} /> : <LockIcon />} onClick={handleLockRate} title={!isLockDeskEnabled ? 'The Lock Desk is currently closed' : ''}>
                                {!isLockDeskEnabled ? 'Lock Desk Closed' : isLocking ? 'Locking Rate...' : 'Lock Rate'}
                              </Button>
                            )}

                            {isLoanLocked && <Alert severity="info" sx={{ mb: 2 }}><strong>Rate is locked</strong>{loan.lockExpirationDate && ` until ${new Date(loan.lockExpirationDate).toLocaleDateString()}`}</Alert>}

                            {isLoanLocked ? (
                              <Card variant="outlined" sx={{ bgcolor: 'rgba(var(--mui-palette-info-mainChannel) / 0.10)', textAlign: 'center' }}>
                                <CardContent sx={{ py: 2 }}>
                                  <LockIcon color="primary" sx={{ fontSize: 28, mb: 1 }} />
                                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Product is locked on this loan</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>To reprice, extend, or change the program, use Post-Lock Actions.</Typography>
                                  {onNavigateTab && <Button variant="contained" fullWidth startIcon={<SettingsIcon />} onClick={() => onNavigateTab('postlock')}>Go to Post-Lock Actions</Button>}
                                </CardContent>
                              </Card>
                            ) : !showAssignConfirm ? (
                              (() => {
                                const productGateDisabled =
                                  isReadOnlyUnlocked && (!assignedProduct || selectedRate?.productShortName !== assignedProduct);
                                const productGateTitle = productGateDisabled
                                  ? 'Read-only loans can only reprice the assigned product'
                                  : (bpcExceedsMaxLpc ? 'BPC amount exceeds the max LPC at this rate' : '');
                                const anyBusy = isAssigning || isAssigningAndLocking;
                                return (
                                  <Stack spacing={1}>
                                    {/* Assign only — preserves the two-step (assign, then lock) path */}
                                    <Button
                                      variant="contained"
                                      fullWidth
                                      startIcon={<LinkIcon />}
                                      onClick={() => { setShowAssignConfirm(true); setAssignError(null); setAssignSuccess(null); setAssignedQuoteId(null); }}
                                      disabled={anyBusy || bpcExceedsMaxLpc || productGateDisabled}
                                      title={productGateTitle}
                                    >
                                      {isReadOnlyUnlocked ? 'Apply Reprice to Assigned Product' : 'Assign Product to Loan'}
                                    </Button>
                                    {/* Assign & Lock — applies pricing and locks in one step */}
                                    <Button
                                      variant="contained"
                                      color="success"
                                      fullWidth
                                      startIcon={isAssigningAndLocking ? <CircularProgress size={16} /> : <LockIcon />}
                                      onClick={handleAssignAndLock}
                                      disabled={anyBusy || bpcExceedsMaxLpc || productGateDisabled}
                                      title={productGateTitle}
                                    >
                                      {isAssigningAndLocking ? 'Assigning & Locking...' : 'Assign & Lock'}
                                    </Button>
                                  </Stack>
                                );
                              })()
                            ) : (
                              <Card variant="outlined" sx={{ borderColor: 'warning.main' }}>
                                <CardContent sx={{ py: 2 }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Confirm Assignment</Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Assign <strong>{selectedRate.productName}</strong> at <strong>{fmtPct(selectedRate.rate)}</strong> to this loan?</Typography>
                                  <Stack direction="row" spacing={1}>
                                    <Button variant="contained" color="warning" sx={{ flex: 1 }} disabled={isAssigning} startIcon={isAssigning ? <CircularProgress size={16} /> : <CheckIcon />}
                                      onClick={async () => {
                                        setIsAssigning(true);
                                        setAssignError(null);
                                        try {
                                          const result = await assignProduct({
                                            loanId,
                                            pricingRequest: lastPricingRequest,
                                            productId: selectedRate.productId,
                                            rate: selectedRate.rate,
                                            price: selectedRate.adjustedPrice,
                                            lockTerm: lockDuration,
                                            ...(selectedRate.quoteId ? { quoteId: selectedRate.quoteId } : {}),
                                            ...(compensationType === 'BPC' && bpcValue ? { bpcCalcMethod, bpcValue: parseFloat(bpcValue) } : {}),
                                          }).unwrap();
                                          if (result?.quoteId) setAssignedQuoteId(result.quoteId);
                                          setAssignSuccess(`${selectedRate.productName} at ${fmtPct(selectedRate.rate)} assigned to the loan.`);
                                          setShowAssignConfirm(false);
                                          setPricingSnapshot(null);
                                          setProducts([]);
                                        } catch (err) {
                                          console.error('[PricingTab] assign-product error:', err);
                                          setAssignError(getAxiosErrorMessage(err, 'Failed to assign product.'));
                                        } finally {
                                          setIsAssigning(false);
                                        }
                                      }}
                                    >
                                      {isAssigning ? 'Assigning...' : 'Confirm'}
                                    </Button>
                                    <Button variant="outlined" onClick={() => setShowAssignConfirm(false)} disabled={isAssigning}>Cancel</Button>
                                  </Stack>
                                </CardContent>
                              </Card>
                            )}
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Box>
                )}
              </>
            );
          })()}
        </>
      )}
    </Box>
  );
};

export default PricingTab;
